import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  BuildingOffice2Icon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

interface Property {
  id: string;
  name: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  propertyGroupId?: string;
  propertyGroup?: {
    id: string;
    name: string;
    slug: string;
  };
  userId?: string | null;
  user?: {
    id: string;
    name: string;
    email: string;
    contactNumber?: string;
  } | null;
  activeAssignment?: {
    id: string;
    monthlyRent: number;
    dueDay: number;
    moveInDate: string;
    moveOutDate?: string | null;
    isActive: boolean;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface ResidentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  contactNumber?: string;
  rentAmount?: number | null;
}

interface PropertyGroup {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface PropertyFormData {
  name: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  propertyGroupId: string;
}

const Properties: React.FC = () => {
  const { user } = useAuth();
  const { groupSlug } = useParams<{ groupSlug?: string }>();
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyGroups, setPropertyGroups] = useState<PropertyGroup[]>([]);
  const [residents, setResidents] = useState<ResidentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [assigningAddress, setAssigningAddress] = useState<Property | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [assignRent, setAssignRent] = useState<string>('');
  const [assignDueDay, setAssignDueDay] = useState<string>('1');
  const [assignMoveIn, setAssignMoveIn] = useState<string>('');
  const [assignMoveOut, setAssignMoveOut] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'properties' | 'residents'>('properties');
  const [formData, setFormData] = useState<PropertyFormData>({
    name: '',
    slug: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyGroupId: ''
  });

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchProperties = useCallback(async () => {
    if (!user?.token) return;
    try {
      const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
      const headers = selectedGroupId ? { 'x-selected-group': selectedGroupId } : {};
      const response = await api.get<Property[]>('/api/addresses', { headers });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setMessage('Failed to load properties');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  const fetchPropertyGroups = useCallback(async () => {
    if (!user?.token) return;
    try {
      const response = await api.get<PropertyGroup[]>('/api/property-groups');
      setPropertyGroups(response.data);
    } catch (error) {
      console.error('Error fetching property groups:', error);
    }
  }, [user?.token]);

  const fetchResidents = useCallback(async () => {
    if (!user?.token) return;
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) return;
    try {
      const response = await api.get<ResidentUser[]>('/api/users', {
        headers: { 'x-selected-group': selectedGroupId }
      });
      setResidents((response.data as ResidentUser[]).filter(u => u.role === 'RESIDENT'));
    } catch (error) {
      console.error('Error fetching residents:', error);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      fetchProperties();
      fetchPropertyGroups();
      fetchResidents();
    } else {
      setLoading(false);
    }
  }, [fetchProperties, fetchPropertyGroups, fetchResidents, user?.role]);

  const handleOpenAssign = (property: Property) => {
    setAssigningAddress(property);
    setSelectedUserId(property.userId || '');
    const a = property.activeAssignment;
    setAssignRent(a && a.monthlyRent ? String(a.monthlyRent) : '');
    setAssignDueDay(a && a.dueDay ? String(a.dueDay) : '1');
    setAssignMoveIn(a && a.moveInDate ? a.moveInDate.slice(0, 10) : '');
    setAssignMoveOut(a && a.moveOutDate ? a.moveOutDate.slice(0, 10) : '');
  };

  const handleAssignUser = async () => {
    if (!assigningAddress) return;
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) return;
    try {
      const response = await api.put<Property>(
        `/api/addresses/${assigningAddress.id}/assign-user`,
        {
          userId: selectedUserId || null,
          monthlyRent: assignRent === '' ? 0 : Number(assignRent),
          dueDay: assignDueDay === '' ? 1 : Number(assignDueDay),
          moveInDate: assignMoveIn || null,
          moveOutDate: assignMoveOut || null,
        },
        { headers: { 'x-selected-group': selectedGroupId } }
      );
      setProperties(prev => prev.map(p => p.id === assigningAddress.id ? { ...p, ...response.data } : p));
      setAssigningAddress(null);
      setSelectedUserId('');
      setMessage(selectedUserId ? 'Tenant assigned successfully' : 'Tenant unassigned successfully');
      setMessageType('success');
      // Refresh residents so computed rent totals on the Tenants tab stay accurate.
      fetchResidents();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to assign tenant');
      setMessageType('error');
    }
  };

  const generateSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();

  const handleNameChange = (name: string) => {
    setFormData(prev => ({ ...prev, name, slug: generateSlug(name) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.token) return;
    try {
      if (editingProperty) {
        await api.put(`/api/addresses/${editingProperty.id}`, formData);
        setMessage('Property updated successfully');
      } else {
        await api.post('/api/addresses', formData);
        setMessage('Property created successfully');
      }
      setMessageType('success');
      setShowAddModal(false);
      setEditingProperty(null);
      resetFormData();
      await fetchProperties();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to save property');
      setMessageType('error');
    }
  };

  const handleEdit = (property: Property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      slug: property.slug,
      address: property.address,
      city: property.city,
      state: property.state,
      zipCode: property.zipCode,
      propertyGroupId: property.propertyGroupId || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (propertyId: string, propertyName: string) => {
    if (!user?.token) return;
    if (!window.confirm(`Delete "${propertyName}"? This will also remove all associated data.`)) return;
    try {
      await api.delete(`/api/addresses/${propertyId}`);
      setMessage('Property deleted');
      setMessageType('success');
      await fetchProperties();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to delete property');
      setMessageType('error');
    }
  };

  const resetFormData = () => {
    setFormData({ name: '', slug: '', address: '', city: '', state: '', zipCode: '', propertyGroupId: '' });
  };

  const resetForm = () => {
    resetFormData();
    setEditingProperty(null);
    setShowAddModal(false);
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee] mx-auto"></div>
        <p className="text-white/60 mt-2">Loading properties...</p>
      </div>
    );
  }

  const getCurrentPropertyGroup = () => {
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    return propertyGroups.find(g => g.id === selectedGroupId)?.name || 'All Properties';
  };

  const occupied = properties.filter(p => p.user);
  const vacant = properties.filter(p => !p.user);
  const occupancyRate = properties.length > 0 ? Math.round((occupied.length / properties.length) * 100) : 0;

  const formatRent = (amount?: number | null) => {
    if (!amount) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const getInitial = (name: string) => name.charAt(0).toUpperCase();

  // Build enriched resident list with their assigned property
  const residentsWithUnits = residents.map(r => ({
    ...r,
    property: properties.find(p => p.userId === r.id)
  }));

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Properties</h1>
          <div className="mt-2 flex items-center space-x-2">
            <span className="text-sm text-white/40">Portfolio:</span>
            <span className="text-sm font-medium text-[#22d3ee] bg-[#22d3ee]/10 px-3 py-1 rounded-full">
              {getCurrentPropertyGroup()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center bg-white/5 rounded-xl p-1 border border-white/10">
            <button
              onClick={() => setActiveTab('properties')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'properties'
                  ? 'bg-[#22d3ee] text-white'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <Squares2X2Icon className="w-4 h-4" />
              Properties
            </button>
            <button
              onClick={() => setActiveTab('residents')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'residents'
                  ? 'bg-[#22d3ee] text-white'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <ListBulletIcon className="w-4 h-4" />
              Tenants
            </button>
          </div>
          <Link
            to={groupSlug ? `/${groupSlug}/bulk-import` : '/bulk-import'}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            <span>Bulk Import</span>
          </Link>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Property</span>
          </button>
        </div>
      </div>

      {/* Summary stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="floating-card py-4 text-center">
          <p className="text-2xl font-bold text-white">{properties.length}</p>
          <p className="text-xs text-white/50 mt-1 uppercase tracking-wide">Total Units</p>
        </div>
        <div className="floating-card py-4 text-center">
          <p className="text-2xl font-bold text-green-400">{occupied.length}</p>
          <p className="text-xs text-white/50 mt-1 uppercase tracking-wide">Occupied</p>
        </div>
        <div className="floating-card py-4 text-center">
          <p className="text-2xl font-bold text-white/50">{vacant.length}</p>
          <p className="text-xs text-white/50 mt-1 uppercase tracking-wide">Vacant</p>
        </div>
        <div className="floating-card py-4 text-center">
          <p className="text-2xl font-bold text-[#22d3ee]">{occupancyRate}%</p>
          <p className="text-xs text-white/50 mt-1 uppercase tracking-wide">Occupancy</p>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-xl border ${
          messageType === 'success'
            ? 'bg-green-500/10 border-green-500/20 text-green-400'
            : 'bg-red-500/10 border-red-500/20 text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* ─── PROPERTIES TAB ─── */}
      {activeTab === 'properties' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {properties.map((property) => (
            <div key={property.id} className="floating-card flex flex-col">

              {/* Card top row: name + badges */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22d3ee]/25 to-[#38bdf8]/25 flex items-center justify-center flex-shrink-0">
                    <BuildingOffice2Icon className="w-5 h-5 text-[#22d3ee]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-white truncate">{property.name}</h3>
                    {property.propertyGroup && (
                      <span className="text-xs text-[#22d3ee]/80 bg-[#22d3ee]/10 px-2 py-0.5 rounded-full">
                        {property.propertyGroup.name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  {/* Occupancy badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    property.user
                      ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                      : 'bg-white/5 text-white/40 border border-white/10'
                  }`}>
                    {property.user ? 'Occupied' : 'Vacant'}
                  </span>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-2 mb-4">
                <MapPinIcon className="w-4 h-4 text-white/30 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/50 leading-snug">
                  <p>{property.address}</p>
                  <p>{property.city}, {property.state} {property.zipCode}</p>
                </div>
              </div>

              {/* Edit / Delete actions */}
              <div className="flex items-center gap-1 mb-4">
                <button
                  onClick={() => handleEdit(property)}
                  className="flex items-center gap-1 text-xs text-white/40 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(property.id, property.name)}
                  className="flex items-center gap-1 text-xs text-white/40 hover:text-red-400 px-2 py-1 rounded hover:bg-red-500/5 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5" /> Delete
                </button>
              </div>

              {/* Tenant section */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <p className="text-xs uppercase tracking-widest text-white/30 mb-3">Tenant</p>

                {property.user ? (
                  <div className="space-y-3">
                    {/* Tenant identity row */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#22d3ee]/30 to-[#38bdf8]/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#22d3ee]">{getInitial(property.user.name)}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">{property.user.name}</p>
                        <p className="text-xs text-white/40 truncate">{property.user.email}</p>
                      </div>
                    </div>

                    {/* Tenant detail chips */}
                    <div className="flex flex-wrap gap-2">
                      {property.user.contactNumber && (
                        <span className="flex items-center gap-1 text-xs text-white/50 bg-white/5 px-2 py-1 rounded-lg">
                          <PhoneIcon className="w-3 h-3" />
                          {property.user.contactNumber}
                        </span>
                      )}
                      {property.activeAssignment && property.activeAssignment.monthlyRent ? (
                        <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-1 rounded-lg font-medium">
                          <CurrencyDollarIcon className="w-3 h-3" />
                          {formatRent(property.activeAssignment.monthlyRent)}/mo
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-white/30 bg-white/5 px-2 py-1 rounded-lg">
                          <CurrencyDollarIcon className="w-3 h-3" />
                          No rent set
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => handleOpenAssign(property)}
                      className="text-xs text-[#22d3ee] hover:text-[#22d3ee]/70 transition-colors"
                    >
                      Change tenant →
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleOpenAssign(property)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/15 hover:border-[#22d3ee]/40 hover:bg-[#22d3ee]/5 transition-all text-sm text-white/35 hover:text-[#22d3ee] group"
                  >
                    <div className="w-7 h-7 rounded-full border border-dashed border-white/20 group-hover:border-[#22d3ee]/40 flex items-center justify-center">
                      <PlusIcon className="w-3.5 h-3.5" />
                    </div>
                    Assign Tenant
                  </button>
                )}
              </div>
            </div>
          ))}

          {properties.length === 0 && (
            <div className="col-span-full text-center py-16">
              <BuildingOffice2Icon className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white/60 mb-2">No Properties Yet</h3>
              <p className="text-white/40 mb-6">Add your first property to start building your portfolio</p>
              <button onClick={() => setShowAddModal(true)} className="btn-primary">
                Add Your First Property
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── TENANTS TAB ─── */}
      {activeTab === 'residents' && (
        <div className="space-y-4">
          {residentsWithUnits.length === 0 ? (
            <div className="text-center py-16">
              <UserIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white/60 mb-2">No Tenants Yet</h3>
              <p className="text-white/40">Add tenants from the Users page, then assign them to a property.</p>
            </div>
          ) : (
            residentsWithUnits.map((resident) => (
              <div key={resident.id} className="floating-card">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#22d3ee]/30 to-[#38bdf8]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-[#22d3ee]">{getInitial(resident.name)}</span>
                  </div>

                  {/* Tenant info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-white">{resident.name}</h3>
                      {resident.property ? (
                        <span className="text-xs bg-green-500/15 text-green-400 border border-green-500/25 px-2 py-0.5 rounded-full">
                          Assigned
                        </span>
                      ) : (
                        <span className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/25 px-2 py-0.5 rounded-full">
                          Unassigned
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-white/50">
                      <span className="flex items-center gap-1">
                        <EnvelopeIcon className="w-3.5 h-3.5" />
                        {resident.email}
                      </span>
                      {resident.contactNumber && (
                        <span className="flex items-center gap-1">
                          <PhoneIcon className="w-3.5 h-3.5" />
                          {resident.contactNumber}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Property + rent */}
                  <div className="hidden sm:flex flex-col items-end gap-1 text-right flex-shrink-0">
                    {resident.property ? (
                      <>
                        <div className="flex items-center gap-1.5 text-sm text-white/80">
                          <BuildingOffice2Icon className="w-4 h-4 text-white/40" />
                          <span className="font-medium">{resident.property.name}</span>
                        </div>
                        <p className="text-xs text-white/40 flex items-center gap-1">
                          <MapPinIcon className="w-3 h-3" />
                          {resident.property.city}, {resident.property.state}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-white/30 italic">No unit assigned</p>
                    )}
                    {resident.rentAmount ? (
                      <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-1 rounded-lg font-medium mt-1">
                        <CurrencyDollarIcon className="w-3 h-3" />
                        {formatRent(resident.rentAmount)}/mo
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Mobile: property info below */}
                {resident.property && (
                  <div className="sm:hidden mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-sm text-white/50">
                    <BuildingOffice2Icon className="w-4 h-4" />
                    <span>{resident.property.name} — {resident.property.city}, {resident.property.state}</span>
                    {resident.rentAmount && (
                      <span className="ml-auto text-green-400 font-medium">{formatRent(resident.rentAmount)}/mo</span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ─── ASSIGN TENANT MODAL ─── */}
      {assigningAddress && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Assign Tenant</h3>
              <button onClick={() => setAssigningAddress(null)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <p className="text-sm text-white/60 mb-4">
              Assign a tenant to <span className="text-white font-medium">{assigningAddress.name}</span>
            </p>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="input-field mb-4"
            >
              <option value="">— No tenant assigned (vacant) —</option>
              {residents.map(r => (
                <option key={r.id} value={r.id}>{r.name} ({r.email})</option>
              ))}
            </select>
            {residents.length === 0 && (
              <p className="text-xs text-white/40 mb-4">No tenants found in this property group. Add residents from the Users page first.</p>
            )}
            {selectedUserId && (
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Monthly Rent</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={assignRent}
                      onChange={(e) => setAssignRent(e.target.value)}
                      placeholder="0.00"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Due Day (1–28)</label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      value={assignDueDay}
                      onChange={(e) => setAssignDueDay(e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Move-In Date</label>
                    <input
                      type="date"
                      value={assignMoveIn}
                      onChange={(e) => setAssignMoveIn(e.target.value)}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Move-Out Date</label>
                    <input
                      type="date"
                      value={assignMoveOut}
                      onChange={(e) => setAssignMoveOut(e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setAssigningAddress(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleAssignUser} className="btn-primary flex-1">
                {selectedUserId ? 'Assign' : 'Mark Vacant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ADD / EDIT PROPERTY MODAL ─── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1b] border border-white/10 rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">
                {editingProperty ? 'Edit Property' : 'Add New Property'}
              </h3>
              <button onClick={resetForm} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <XMarkIcon className="w-5 h-5 text-white/60" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Property Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="input-field"
                  placeholder="Unit 4B / 123 Main St"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">URL Slug *</label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="input-field"
                  placeholder="unit-4b"
                />
                <p className="text-xs text-white/40 mt-1">Auto-generated from name.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Street Address *</label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="input-field"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="input-field"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">State *</label>
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className="input-field"
                    placeholder="State"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">ZIP Code *</label>
                <input
                  type="text"
                  required
                  value={formData.zipCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="input-field"
                  placeholder="12345"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Property Group</label>
                <select
                  value={formData.propertyGroupId}
                  onChange={(e) => setFormData(prev => ({ ...prev, propertyGroupId: e.target.value }))}
                  className="input-field"
                >
                  <option value="">No Group (Uncategorized)</option>
                  {propertyGroups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center space-x-2">
                  <CheckIcon className="w-4 h-4" />
                  <span>{editingProperty ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Properties;
